import { Box, Grid } from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'

import useFollowUpListagem from '@hook/followUpFornecedor/use-listagem'
import useFilters from '@hook/shared/use-filters'
import useLayoutHeight from '@hook/shared/use-layout-height'
import useUserRole from '@hook/shared/use-user-role'

import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import LayoutTools from '@component/Layout/Tools'
import HistoricoModal, {
  HistoricoState
} from '@component/FollowUpFornecedor/HistoricoModal'

import followUpFornecedorService, {
  StatusAprovacao,
  StatusDisparo
} from '@service/followUpFornecedor'

import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'

import KpiCards from './KpiCards'
import ListagemTable from './ListagemTable'

const FollowUpFornecedorListagem: FC = () => {
  const height = useLayoutHeight()
  const { isAdmin } = useUserRole()
  const { getFilters } = useFilters()

  const {
    loading,
    kpiLoading,
    data,
    kpi,
    query,
    params,
    pagination,
    currentyDate,
    getData,
    handleUpdateParams,
    handlePageChange,
    setQuery
  } = useFollowUpListagem()

  const [showFilters, setShowFilters] = useState(false)
  const [userOptions, setUserOptions] = useState<
    { value: number; label: string }[]
  >([])

  const [historico, setHistorico] = useState<HistoricoState>({
    open: false,
    loading: false,
    nomeProduto: '',
    documentId: null,
    rodadas: []
  })

  const handleOpenHistorico = async (item: {
    documentId: number
    productId: number
    nomeProduto: string
  }) => {
    setHistorico(h => ({
      ...h,
      open: true,
      loading: true,
      nomeProduto: item.nomeProduto,
      documentId: item.documentId,
      rodadas: []
    }))
    try {
      const { data: res } = await followUpFornecedorService.getHistoricoPar({
        documentId: item.documentId,
        productId: item.productId
      })
      setHistorico(h => ({ ...h, loading: false, rodadas: res.data ?? [] }))
    } catch {
      setHistorico(h => ({ ...h, loading: false }))
    }
  }

  useEffect(() => {
    if (isAdmin) {
      followUpFornecedorService
        .getUsuariosResponsaveis()
        .then(({ data: res }) => {
          setUserOptions(
            (res.data ?? [])
              .filter((u: any) => u.id != null)
              .map((u: any) => ({
                value: Number(u.id),
                label: String(u.email)
              }))
          )
        })
        .catch(() => {})
    }
    getData()
    getFilters({ providersForPurchase: true, centers: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const actions: ILayoutToolsAction[] = [{ type: 'followUpFornecedorExport' }]

  const clearAllFilters = () => {
    handleUpdateParams('fornecedores', [])
    handleUpdateParams('centros', [])
    handleUpdateParams('produtos', [])
    handleUpdateParams('pedidos', [])
    handleUpdateParams('statusDisparo', [])
    handleUpdateParams('statusAprovacao', [])
    handleUpdateParams('initialDate', '')
    handleUpdateParams('finalDate', '')
    handleUpdateParams('usuarios', [])
    getData()
  }

  const filtersList = [
    {
      id: 'fornecedores',
      label: 'Fornecedor',
      filterId: 'providersForPurchase',
      type: 'select',
      isMulti: true,
      value: params.fornecedores
    },
    {
      id: 'centros',
      label: 'Centro',
      filterId: 'centers',
      type: 'select',
      isMulti: true,
      value: params.centros
    },
    {
      id: 'produtos',
      label: 'Produto',
      type: 'select-api-post',
      apiUrl: 'v1/Produto',
      isMulti: true,
      value: params.produtos
    },
    {
      id: 'pedidos',
      label: 'Pedidos',
      type: 'select-api-post',
      apiUrl: '/v1/PedidoCompra',
      idsField: 'pedidosIds',
      labelFields: ['descricao'],
      isMulti: true,
      value: params.pedidos
    },
    {
      id: 'statusDisparo',
      label: 'Status Disparo',
      type: 'select-pagination',
      isMulti: true,
      options: [
        { label: 'Aguardando Envio', value: StatusDisparo.AguardandoEnvio },
        { label: 'E-mail Enviado', value: StatusDisparo.EmailEnviado },
        { label: 'Submetido', value: StatusDisparo.Submetido },
        { label: 'Inválido', value: StatusDisparo.Invalido },
        { label: 'Expirado', value: StatusDisparo.Expirado },
        { label: 'Erro no Envio', value: StatusDisparo.ErroEnvio }
      ],
      value: params.statusDisparo
    },
    {
      id: 'statusAprovacao',
      label: 'Status Aprovação',
      type: 'select-pagination',
      isMulti: true,
      options: [
        { label: 'Aguardando', value: StatusAprovacao.Aguardando },
        { label: 'Aprovado', value: StatusAprovacao.Aprovado },
        { label: 'Recusado', value: StatusAprovacao.Recusado }
      ],
      value: params.statusAprovacao
    },
    {
      id: 'period',
      label: 'Período do Disparo',
      type: 'date-input'
    },
    ...(isAdmin
      ? [
          {
            id: 'usuarios',
            label: 'Usuário Responsável',
            type: 'select-pagination',
            isMulti: true,
            options: userOptions
          }
        ]
      : [])
  ]

  const filtered = query
    ? data.filter(i => {
        const q = query.toLowerCase()
        return (
          i.nomeProduto.toLowerCase().includes(q) ||
          i.fornecedor.toLowerCase().includes(q) ||
          i.centro.toLowerCase().includes(q) ||
          String(i.productId).includes(q) ||
          String(i.documentId).includes(q) ||
          (i.observacaoAprovador ?? '').toLowerCase().includes(q)
        )
      })
    : data

  return (
    <Layout title="Follow Up Fornecedor — Painel Geral">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!showFilters ? '100vw' : '260px calc(100vw - 260px)'}
      >
        {showFilters && (
          <LayoutFilters
            filters={filtersList as any}
            values={params}
            defaultDate={currentyDate}
            onChange={({ name, value }) => handleUpdateParams(name, value)}
            loading={loading}
            canCancel={loading}
            onFilter={getData}
            onClearAllFilters={clearAllFilters}
          />
        )}

        <Box overflow="hidden" display="flex" flexDirection="column" h="100%">
          <LayoutTools
            title="Follow Up Fornecedor"
            actions={actions}
            filters={showFilters}
            query={query}
            onSearchChange={setQuery}
            toggleFilters={() => setShowFilters(f => !f)}
            isDisabled={loading || data.length === 0}
          />

          <KpiCards kpi={kpi} kpiLoading={kpiLoading} />

          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : filtered.length === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <Box flex={1} overflow="auto" px={4} pt={2}>
              <ListagemTable
                filtered={filtered}
                onOpenHistorico={handleOpenHistorico}
              />
            </Box>
          )}

          {pagination.totalPages > 1 && !loading && (
            <Box px={4} py={2} borderTop="1px solid" borderColor="gray.100">
              <ReactPaginate
                previousLabel={'Anterior'}
                nextLabel={'Próximo'}
                pageCount={Number(pagination.totalPages)}
                forcePage={Number(pagination.actualPage) - 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                activeClassName={'active'}
                containerClassName="pagination"
                onPageChange={({ selected }) => handlePageChange(selected)}
              />
            </Box>
          )}
        </Box>
      </Grid>

      <HistoricoModal
        historico={historico}
        onClose={() => setHistorico(h => ({ ...h, open: false }))}
      />
    </Layout>
  )
}

export default FollowUpFornecedorListagem
